import { spanFromRegExpMatch } from '../spans';
import type { ExtractorOutput } from './types';
import type { MatchSpan } from '../types';

const NUMBER_TOKEN = '\\$?\\s*(\\d+(?:[.,]\\d+)?)\\s*([km])?';

function parseAmount(raw: string, unitHint?: string): number {
  const value = parseFloat(raw);
  const unit = (unitHint || '').toLowerCase();
  if (unit === 'm') return value * 1_000_000;
  if (unit === 'k') return value * 1_000;
  // Bare "$800" in search context almost always means $800k
  if (value < 1_000) return value * 1_000;
  return value;
}

/**
 * Mask sqft phrases so "over 2000 sqft" is not misread as a price.
 * Replaces matched characters with spaces to preserve indices.
 */
function maskSqftPhrases(query: string): string {
  return query.replace(/\d+\s*(?:to\s+\d+\s*)?(?:sq\s*ft|sqft|square\s+(?:feet|foot)|sf)\b/gi, match =>
    ' '.repeat(match.length)
  );
}

/**
 * Mask maintenance/HOA fee phrases so "maintenance under $500" is not
 * misread as listing priceMax.
 */
function maskMaintenanceFeePhrases(query: string): string {
  const feeLabel =
    '(?:maintenance(?:\\s+fees?)?|hoa(?:\\s+fees?)?|condo\\s+fees?|strata(?:\\s+fees?)?)';
  const feeNumber = '\\$?\\s*\\d+(?:[.,]\\d+)?\\s*[km]?';
  const patterns = [
    new RegExp(
      `${feeLabel}\\s*(?:of\\s+)?(?:under|below|less\\s+than|max(?:imum)?|up\\s+to|<=?)\\s*${feeNumber}`,
      'gi'
    ),
    new RegExp(
      `(?:under|below|less\\s+than|max(?:imum)?|up\\s+to|<=?)\\s*${feeNumber}\\s*(?:(?:per\\s+month|\\/mo|monthly)\\s+)?${feeLabel}`,
      'gi'
    ),
    new RegExp(`(?:max(?:imum)?\\s+)?${feeLabel}\\s*(?:of\\s+|is\\s+|at\\s+)?${feeNumber}`, 'gi'),
    /\blow\s+maintenance(?:\s+fees?)?\b/gi,
  ];

  let masked = query;
  for (const pattern of patterns) {
    masked = masked.replace(pattern, match => ' '.repeat(match.length));
  }
  return masked;
}

export function extractPriceRange(query: string): ExtractorOutput {
  const masked = maskMaintenanceFeePhrases(maskSqftPhrases(query));
  const hasMoneySignal =
    /[$]|\d\s*[km]\b|\b(under|over|between|budget|up\s+to|at\s+least|maximum|minimum|max|min|from)\b/i.test(
      masked
    );
  if (!hasMoneySignal) return { partial: {}, spans: [] };

  const rangePatterns = [
    { pattern: new RegExp(`between\\s+${NUMBER_TOKEN}\\s+(?:and|to|-)\\s+${NUMBER_TOKEN}`, 'i'), ruleId: 'price.between' },
    { pattern: new RegExp(`${NUMBER_TOKEN}\\s+to\\s+${NUMBER_TOKEN}`, 'i'), ruleId: 'price.to' },
    { pattern: new RegExp(`${NUMBER_TOKEN}\\s*-\\s*${NUMBER_TOKEN}`, 'i'), ruleId: 'price.hyphen' },
  ];

  for (const { pattern, ruleId } of rangePatterns) {
    const match = masked.match(pattern);
    if (!match || match.index === undefined) continue;
    const priceMin = parseAmount(match[1].replace(/,/g, ''), match[2]);
    const priceMax = parseAmount(match[3].replace(/,/g, ''), match[4]);
    if (priceMin >= priceMax) continue;

    const span = spanFromRegExpMatch(query, match, 'price', { priceMin, priceMax }, {
      confidence: 0.95,
      ruleId,
    });
    return {
      partial: { priceMin, priceMax },
      spans: [span],
    };
  }

  const maxPattern = new RegExp(
    `(?:under|below|less\\s+than|max(?:imum)?|up\\s+to|<=?)\\s*${NUMBER_TOKEN}`,
    'i'
  );
  const maxMatch = masked.match(maxPattern);
  if (maxMatch && maxMatch.index !== undefined) {
    const priceMax = parseAmount(maxMatch[1].replace(/,/g, ''), maxMatch[2]);
    return {
      partial: { priceMax },
      spans: [
        spanFromRegExpMatch(query, maxMatch, 'priceMax', priceMax, {
          confidence: 0.95,
          ruleId: 'price.max',
        }),
      ],
    };
  }

  const minPattern = new RegExp(
    `(?:over|above|more\\s+than|min(?:imum)?|at\\s+least|from|>=?)\\s*${NUMBER_TOKEN}`,
    'i'
  );
  const minMatch = masked.match(minPattern);
  if (minMatch && minMatch.index !== undefined) {
    const priceMin = parseAmount(minMatch[1].replace(/,/g, ''), minMatch[2]);
    return {
      partial: { priceMin },
      spans: [
        spanFromRegExpMatch(query, minMatch, 'priceMin', priceMin, {
          confidence: 0.95,
          ruleId: 'price.min',
        }),
      ],
    };
  }

  const bareWithUnit = masked.match(/\$\s*(\d+(?:[.,]\d+)?)\s*([km])?\b|\b(\d+(?:[.,]\d+)?)\s*([km])\b/i);
  if (bareWithUnit && bareWithUnit.index !== undefined) {
    const numStr = bareWithUnit[1] ?? bareWithUnit[3];
    const unit = bareWithUnit[2] ?? bareWithUnit[4];
    if (numStr) {
      const priceMax = parseAmount(numStr.replace(/,/g, ''), unit);
      const span: MatchSpan = spanFromRegExpMatch(query, bareWithUnit, 'priceMax', priceMax, {
        confidence: 0.85,
        ruleId: 'price.bare-max',
      });
      return { partial: { priceMax }, spans: [span] };
    }
  }

  return { partial: {}, spans: [] };
}
